import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Eye, Search, Calendar, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step 6 redesign — DeliveryNotes is now used by TWO routes:
//   /operations/delivery-notes/today      -> mode='today'
//   /operations/delivery-notes/historical -> mode='historical'
// Each route shows only its slice of notes plus the four lifecycle
// sub-sections (Open / Completed / Sent / Voided). Less clutter, clearer
// daily view. The empty Voided sub-section is collapsed by default.

const fmtDateLong = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('en-GB'); } catch { return s; }
};

const localTodayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const lifecycleOf = (n) => {
  const ls = n?.lifecycle_status;
  if (ls) return ls;
  if (n?.status === 'sent') return 'sent';
  return 'completed';
};

const SubSectionHeader = ({ title, count, color, expanded, onToggle, hint, warning }) => (
  <button
    onClick={onToggle}
    className="w-full flex items-center justify-between mb-2 px-4 py-3 bg-white rounded-xl shadow-sm hover:bg-gray-50"
    style={{ borderLeft: `4px solid ${color}` }}
  >
    <div className="flex items-center gap-3">
      {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      <span className="font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>{title}</span>
      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: color, color: '#fff' }}>{count}</span>
      {warning && (
        <span className="flex items-center gap-1 text-xs text-amber-700 ml-2">
          <AlertTriangle size={14} /> {warning}
        </span>
      )}
      {hint && !warning && <span className="text-xs text-gray-500 ml-2">{hint}</span>}
    </div>
  </button>
);

const NoteRow = ({ note, todayIso }) => {
  const skipCount = note.skips?.length ?? 0;
  const itemCount = note.skips?.reduce(
    (a, s) => a + (s.items?.reduce((b, i) => b + (i.quantity || 0), 0) || 0), 0
  ) ?? 0;
  const stale = lifecycleOf(note) === 'open' && note.request_date && note.request_date < todayIso;
  return (
    <Link
      to={`/operations/delivery-notes/${note.id}`}
      className={`flex items-center justify-between px-4 py-3 bg-white border rounded-lg hover:bg-gray-50 hover:border-gray-300 ${stale ? 'border-amber-300' : 'border-gray-200'}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {stale && <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />}
          <span className="text-sm font-mono text-gray-500">#{note.note_number}</span>
          <span className="text-base font-medium text-gray-800 truncate">{note.customer_name || `Customer ${note.customer_id}`}</span>
          <span className="text-xs text-gray-400 font-mono">{note.customer_code || ''}</span>
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-3">
          <span><Calendar size={12} className="inline mr-1" />Processed {fmtDateLong(note.request_date)}</span>
          <span>· Delivered {fmtDateLong(note.delivery_date)}</span>
          <span>· {skipCount} skip{skipCount === 1 ? '' : 's'}</span>
          <span>· {itemCount} item{itemCount === 1 ? '' : 's'}</span>
        </div>
      </div>
      <Eye size={16} className="text-gray-400 ml-4" />
    </Link>
  );
};

const LIFECYCLE_META = {
  open:      { title: 'Open',      color: '#0C447C', hint: 'Skips can still be added or edited' },
  completed: { title: 'Completed', color: '#854F0B', hint: 'Locked, ready to email' },
  sent:      { title: 'Sent',      color: '#3B6D11', hint: 'Emailed to customer' },
  voided:    { title: 'Voided',    color: '#5F5E5A', hint: 'Cancelled, kept for audit' },
};

const DeliveryNotes = ({ mode = 'today' }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [subExpanded, setSubExpanded] = useState({
    open: true, completed: true, sent: true, voided: false,
  });
  const todayIso = localTodayIso();

  useEffect(() => { fetchNotes(); }, []);

  const fetchNotes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/operations/delivery-notes`, { credentials: 'include' });
      const data = await res.json();
      const sorted = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotes(sorted);
    } catch (e) {
      console.error('Error fetching delivery notes:', e);
    } finally {
      setLoading(false);
    }
  };

  const matchesFilters = (note) => {
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      const haystack = [
        String(note.note_number || ''),
        note.customer_name || '',
        note.customer_code || '',
      ].join(' ').toLowerCase();
      if (!haystack.includes(term)) return false;
    }
    if (dateFrom && note.delivery_date < dateFrom) return false;
    if (dateTo && note.delivery_date > dateTo) return false;
    return true;
  };

  const inMode = (note) => {
    // 'today' includes future-dated notes too (work in progress that needs visibility).
    const isToday = !note.request_date || note.request_date >= todayIso;
    return mode === 'today' ? isToday : !isToday;
  };

  const grouped = { open: [], completed: [], sent: [], voided: [] };
  notes.filter(matchesFilters).filter(inMode).forEach(n => {
    const ls = lifecycleOf(n);
    if (grouped[ls]) grouped[ls].push(n);
  });

  const toggle = (key) => setSubExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const isHistorical = mode === 'historical';
  const staleOpenCount = isHistorical ? grouped.open.length : 0;
  const pageTitle = isHistorical ? 'Historical Delivery Notes' : "Today's Delivery Notes";
  const pageSubtitle = isHistorical
    ? 'All delivery notes from previous processing days. Use the date filter to narrow down.'
    : `Processing date ${fmtDateLong(todayIso)}. Notes for today plus any future-dated work.`;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-lg text-gray-600">Loading...</div></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>{pageTitle}</h1>
          <p className="text-gray-600 mt-1">{pageSubtitle}</p>
        </div>
        {!isHistorical && (
          <Link
            to="/operations/delivery-notes/new"
            className="flex items-center px-4 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332]"
          >
            <Plus size={18} className="mr-2" /> New Delivery Note
          </Link>
        )}
      </div>

      {/* Stale-open warning banner — only on Historical */}
      {isHistorical && staleOpenCount > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>{staleOpenCount} unfinished note{staleOpenCount === 1 ? '' : 's'} from earlier days.</strong>
            {' '}Open the Open section below to finish them.
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6 bg-white p-4 rounded-xl shadow-sm">
        <div className="flex-1 min-w-[220px] relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by note #, customer name or code"
            className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1F2D3D]"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Delivery date:</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 border rounded-lg" />
          <span className="text-sm text-gray-400">to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 border rounded-lg" />
        </div>
      </div>

      {/* Lifecycle sub-sections */}
      {['open', 'completed', 'sent', 'voided'].map(ls => {
        const meta = LIFECYCLE_META[ls];
        const list = grouped[ls];
        const expanded = subExpanded[ls];
        const warning = (isHistorical && ls === 'open' && list.length > 0)
          ? `${list.length} from earlier days still open`
          : null;
        return (
          <div key={ls} className="mb-6">
            <SubSectionHeader
              title={meta.title} color={meta.color} count={list.length}
              hint={meta.hint} warning={warning}
              expanded={expanded} onToggle={() => toggle(ls)}
            />
            {expanded && (
              <div className="space-y-2">
                {list.length === 0
                  ? <div className="text-sm text-gray-500 italic px-4 py-3">No notes in this section.</div>
                  : list.map(n => <NoteRow key={n.id} note={n} todayIso={todayIso} />)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DeliveryNotes;
